// client/src/lib/OrgProvider.js
//
// Fetches /me once at app startup and exposes the result to the whole
// component tree via React Context. After this exists, NO component
// should call useFetch('/me') directly — they should call useMe() or
// useOrg() and get the cached result.
//
// This also becomes the single place where palette + document title
// will be set from the active org. (Wired up in a later step.)

import { createContext, useContext } from 'react';
import { useFetch } from '../hooks/useFetch';

const OrgContext = createContext(null);

export function OrgProvider({ children }) {
  const { data: me, loading, error } = useFetch('/me');

  // Render gates: don't let children mount until we know who the user
  // is. This means downstream code can assume useMe() returns a real
  // user object, never null/undefined.
  if (loading) {
    return (
      <div style={{ padding: 20, fontFamily: 'sans-serif' }}>Loading…</div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 20, fontFamily: 'sans-serif', color: 'crimson' }}>
        <h2>Something went wrong</h2>
        <pre>{error}</pre>
      </div>
    );
  }

  // Resolve the active membership (the one matching active_org_id).
  // Can be null if active_org_id is unset — downstream code that uses
  // activeOrg must handle that case (e.g. palette falls back to default).
  const activeOrg =
    me.memberships.find((m) => m.org_id === me.active_org_id) || null;

  return (
    <OrgContext.Provider value={{ me, activeOrg }}>
      {children}
    </OrgContext.Provider>
  );
}

// Hooks for consumers. Two flavors because callers usually want either
// just the user, or the user + active org — not the raw context object.
export function useOrg() {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used inside <OrgProvider>');
  return ctx;
}

export function useMe() {
  return useOrg().me;
}